import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const api = axios.create({
  baseURL: `${window.location.origin}/api`,
  withCredentials: true,
});
export { api };

// Download file with original filename - fallback to static URL if download endpoint fails
export const downloadFileWithName = async (downloadUrl, fallbackUrl, filename) => {
  try {
    const res = await fetch(downloadUrl, { credentials: 'include' });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    // Fallback: open static URL directly
    if (fallbackUrl) {
      const a = document.createElement('a');
      a.href = `${window.location.origin}${fallbackUrl}`;
      a.download = filename || '';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }
};

// Open authenticated download in new tab using a short-lived ?token= param.
// Works across domains and in mobile Chrome where new-tab cookies are unreliable.
let _dlTokenCache = { token: null, expires_at: 0 };
export const openAuthenticatedDownload = async (relativePath, filename) => {
  try {
    const now = Date.now();
    if (!_dlTokenCache.token || _dlTokenCache.expires_at < now) {
      const { data } = await api.get('/download-token');
      _dlTokenCache = {
        token: data.token,
        expires_at: now + (data.expires_in - 20) * 1000,
      };
    }
    const sep = relativePath.includes('?') ? '&' : '?';
    const url = `${window.location.origin}${relativePath}${sep}token=${encodeURIComponent(_dlTokenCache.token)}`;
    const a = document.createElement('a');
    a.href = url;
    if (filename) a.download = filename;
    a.rel = 'noopener noreferrer';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (err) {
    console.error('Download failed:', err);
    throw err;
  }
};

// Staff
export const getStaff = () => api.get('/staff');
export const createStaff = (data) => api.post('/staff', data);
export const deleteStaff = (id) => api.delete(`/staff/${id}`);
export const getSubStaff = (params) => api.get('/sub-staff', { params });
export const createSubStaff = (data) => api.post('/sub-staff', data);
export const deleteSubStaff = (id) => api.delete(`/sub-staff/${id}`);
export const resetSubStaffPassword = (id, password) => api.post(`/sub-staff/${id}/reset-password`, { password });
export const getSubStaffActivity = (id) => api.get(`/sub-staff/${id}/activity`);

// Assistants
export const getAssistants = () => api.get('/assistants');
export const createAssistant = (data) => api.post('/assistants', data);
export const deleteAssistant = (id) => api.delete(`/assistants/${id}`);

// User Approval
export const getPendingUsers = () => api.get('/users/pending');
export const getPendingUsersCount = () => api.get('/users/pending-count');
export const updateUserStatus = (userId, action) => api.put(`/users/${userId}/status`, { action });

// Agencies
export const getAgencies = () => api.get('/agencies');
export const createAgency = (data) => api.post('/agencies', data);
export const updateAgency = (id, data) => api.put(`/agencies/${id}`, data);
export const deleteAgency = (id) => api.delete(`/agencies/${id}`);

// Deposits
export const getDeposits = (staffId) => api.get('/deposits', { params: { staff_id: staffId } });
export const createDeposit = (data) => api.post('/deposits', data);
export const deleteDeposit = (id) => api.delete(`/deposits/${id}`);

// Office Ledger (salary / incentive / office_expense / owner_expense)
export const getOfficeLedger = (params) => api.get('/office-ledger', { params });
export const getOfficeLedgerSummary = (params) => api.get('/office-ledger/summary', { params });
export const getOfficeLedgerRecipients = () => api.get('/office-ledger/recipients');
export const createOfficeLedger = (data) => api.post('/office-ledger', data);
export const updateOfficeLedger = (id, data) => api.put(`/office-ledger/${id}`, data);
export const deleteOfficeLedger = (id) => api.delete(`/office-ledger/${id}`);

// Staff Ledger Audit (deposits vs spend per-agency)
export const getStaffLedger = (params) => api.get('/staff-ledger/audit', { params });

// Auto-Assign (booking files)
export const getAutoAssignSettings = () => api.get('/auto-assign/settings');
export const getAutoAssignRecentActivity = (days = 7, limit = 200) => api.get(`/auto-assign/recent-activity?days=${days}&limit=${limit}`);
export const toggleAutoAssign = (enabled) => api.put('/auto-assign/toggle', { enabled });
export const updateAutoAssignStaffConfig = (staffId, data) => api.put(`/auto-assign/staff/${staffId}`, data);
export const runAutoAssignOnFile = (fileId) => api.post(`/auto-assign/run/${fileId}`);
export const runAutoAssignPending = () => api.post('/auto-assign/run-pending');
export const getAmazonAutoLockSetting = () => api.get('/auto-assign/amazon-lock');
export const toggleAmazonAutoLock = (enabled) => api.put('/auto-assign/amazon-lock/toggle', { enabled });

// Bookings
export const getBookings = (staffId, status, startDate, endDate, agencyId) => api.get('/bookings', { params: { staff_id: staffId, status, start_date: startDate, end_date: endDate, agency_id: agencyId } });
export const exportBookingsExcel = (staffId, startDate, endDate, agencyId) => api.get('/bookings/export-excel', { params: { staff_id: staffId, start_date: startDate, end_date: endDate, agency_id: agencyId }, responseType: 'blob' });
export const exportBookingsPdf = (staffId, startDate, endDate, agencyId) => api.get('/bookings/export-pdf', { params: { staff_id: staffId, start_date: startDate, end_date: endDate, agency_id: agencyId }, responseType: 'blob' });
export const createBooking = (data) => api.post('/bookings', data);
export const checkBookingDuplicate = (agencyName, quantity, staffId) => api.post(`/bookings/check-duplicate?agency_name=${encodeURIComponent(agencyName)}&quantity=${quantity}${staffId ? `&staff_id=${staffId}` : ''}`);

// Refunds
export const getRefunds = (staffId) => api.get('/refunds', { params: { staff_id: staffId } });
export const createRefund = (data) => api.post('/refunds', data);
export const getBookingRefundInfo = (bookingId) => api.get(`/bookings/${bookingId}/refund-info`);

// Amazon Refunds (New System)
export const getAmazonRefunds = (staffId, status) => api.get('/amazon-refunds', { params: { staff_id: staffId, status } });
export const getPendingAmazonRefundsCount = () => api.get('/amazon-refunds/pending-count');
export const createAmazonRefund = (formData) => api.post('/amazon-refunds', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const reviewAmazonRefund = (refundId, data) => api.put(`/amazon-refunds/${refundId}`, data);
export const getAmazonAccounts = () => api.get('/amazon-accounts');
export const uploadFile = (formData) => api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Amazon Balance
export const getAmazonBalances = (staffId) => api.get('/amazon-balance', { params: { staff_id: staffId } });
export const updateAmazonBalance = (staffId, amount) => api.put(`/amazon-balance/${staffId}`, null, { params: { amount } });
export const useAmazonBalance = (staffId, amount) => api.post(`/amazon-balance/${staffId}/use`, null, { params: { amount } });

// Card Balance
export const getCardBalances = (staffId) => api.get('/card-balance', { params: { staff_id: staffId } });
export const cardBalanceTransaction = (data) => api.post('/card-balance/transaction', data);
export const getBalanceTransactions = (staffId) => api.get('/card-balance/transactions', { params: { staff_id: staffId } });

// Daily Balance
export const getDailyBalances = (staffId, date, startDate, endDate) => 
  api.get('/daily-balance', { params: { staff_id: staffId, date, start_date: startDate, end_date: endDate } });
export const initializeDailyBalance = (staffId, date) => 
  api.post('/daily-balance/initialize', null, { params: { staff_id: staffId, date } });
export const setOpeningBalance = (staffId, openingBalance, date) => 
  api.put('/daily-balance/opening', null, { params: { staff_id: staffId, opening_balance: openingBalance, date } });
export const getDailyTransactions = (staffId, date, startDate, endDate) => 
  api.get('/daily-transactions', { params: { staff_id: staffId, date, start_date: startDate, end_date: endDate } });
export const getStaffDailyReport = (date, startDate, endDate) => 
  api.get('/daily-report/staff', { params: { date, start_date: startDate, end_date: endDate } });
export const getAgencyDailyReport = (date, startDate, endDate) => 
  api.get('/daily-report/agency', { params: { date, start_date: startDate, end_date: endDate } });

// Fund Requests
export const getFundRequests = (status) => api.get('/fund-requests', { params: { status } });
export const getPendingFundRequestsCount = () => api.get('/fund-requests/pending-count');
export const createFundRequest = (data) => api.post('/fund-requests', data);
export const reviewFundRequest = (requestId, data) => api.put(`/fund-requests/${requestId}`, data);
export const reverseFundRequest = (requestId, data) => api.post(`/fund-requests/${requestId}/reverse`, data);
export const cancelFundRequest = (requestId) => api.delete(`/fund-requests/${requestId}`);

// Booking Confirm
export const confirmBooking = (bookingId) => api.post(`/bookings/${bookingId}/confirm`);

// Booking Reverse
export const reverseBooking = (bookingId, data) => api.post(`/bookings/${bookingId}/reverse`, data);
export const partialRefundBooking = (bookingId, data) => api.post(`/bookings/${bookingId}/partial-refund`, data);
export const getTxnHistory = (txnId) => api.get(`/bookings/txn-history/${txnId}`);

// Booking Edit/Delete (Staff corrections)
export const editBooking = (bookingId, data) => api.put(`/bookings/${bookingId}/edit`, data);
export const deleteBooking = (bookingId, reason) => api.delete(`/bookings/${bookingId}`, { params: { reason } });

// Staff Ledger
// Staff Ledger (flat per-booking list — legacy)
export const getStaffLedgerFlat = (staffId, startDate, endDate) => api.get('/staff-ledger', { params: { staff_id: staffId, start_date: startDate, end_date: endDate } });

// Bulk Bookings
export const parseBulkBooking = (data) => api.post('/bookings/bulk/parse', data);
export const parseBulkExcel = (formData) => api.post('/bookings/bulk/parse-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const uploadBulkEntryReport = (formData) => api.post('/bulk-entry/upload-report', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const saveBulkBookings = (data) => api.post('/bookings/bulk/save', data);

// Agency Search (Fuzzy)
export const searchAgencies = (q) => api.get('/agencies/search', { params: { q } });

// Notifications
export const getNotifications = () => api.get('/notifications');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);

// Pending Agencies
export const getPendingAgencies = () => api.get('/agencies/pending');
export const approveAgency = (id, data = {}) => api.put(`/agencies/${id}/approve`, data);
export const bulkImportAgencies = (formData) => api.post('/agencies/bulk-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const saveBulkAgencies = (data) => api.post('/agencies/bulk-import/save', data);

// Audit Logs
export const getAuditLogs = (entityType, actionType, staffId, limit) => 
  api.get('/audit-logs', { params: { entity_type: entityType, action_type: actionType, staff_id: staffId, limit } });

// Payment Sources
export const getPaymentSources = () => api.get('/payment-sources');
export const createPaymentSource = (data) => api.post('/payment-sources', data);

// Dashboard
export const getDashboardStats = (staffId) => api.get('/dashboard/stats', { params: { staff_id: staffId } });
export const getOwnerDashboardSummary = () => api.get('/dashboard/owner-summary');

// Reports
export const getBookingsReport = (staffId, startDate, endDate, agencyId) => api.get('/reports/bookings', { params: { staff_id: staffId, start_date: startDate, end_date: endDate, agency_id: agencyId } });

// Wallet Statement
export const getWalletStatement = (staffId, startDate, endDate) => 
  api.get('/wallet-statement', { params: { staff_id: staffId, start_date: startDate, end_date: endDate } });
export const exportWalletExcel = (staffId, startDate, endDate) => 
  api.get('/wallet-statement/export-excel', { params: { staff_id: staffId, start_date: startDate, end_date: endDate }, responseType: 'blob' });
export const exportWalletPdf = (staffId, startDate, endDate) => 
  api.get('/wallet-statement/export-pdf', { params: { staff_id: staffId, start_date: startDate, end_date: endDate }, responseType: 'blob' });

// Distributor Ledger
export const getDistributorLedger = (agencyId, startDate, endDate, txnType, doneByRole, search) =>
  api.get('/distributor-ledger', { params: { agency_id: agencyId, start_date: startDate, end_date: endDate, txn_type: txnType, done_by_role: doneByRole, search: search } });
export const getDistributorBalance = () => api.get('/distributor-balance');
export const exportDistributorExcel = (agencyId, startDate, endDate, txnType) =>
  api.get('/distributor-ledger/export-excel', { params: { agency_id: agencyId, start_date: startDate, end_date: endDate, txn_type: txnType }, responseType: 'blob' });
export const exportDistributorPdf = (agencyId, startDate, endDate, txnType) =>
  api.get('/distributor-ledger/export-pdf', { params: { agency_id: agencyId, start_date: startDate, end_date: endDate, txn_type: txnType }, responseType: 'blob' });

// Distributor Management (Owner)
export const getDistributors = () => api.get('/distributors');
export const createDistributor = (data) => api.post('/distributors', data);
export const updateDistributor = (id, data) => api.put(`/distributors/${id}`, data);
export const deleteDistributor = (id) => api.delete(`/distributors/${id}`);
export const downloadDistributorTemplate = () => api.get('/distributors/template', { responseType: 'blob' });
export const bulkUploadDistributors = (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/distributors/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const updateUserProfile = (userId, data) => api.put(`/users/${userId}/profile`, data);

// Distributor Portal
export const getDistributorDashboard = () => api.get('/distributor/dashboard');
export const getDistributorFundRequests = (status) => api.get('/distributor/fund-requests', { params: { status } });
export const getDistributorFundRequestsPendingCount = () => api.get('/distributor/fund-requests/pending-count');
export const getAgencyLedgerSummary = () => api.get('/distributor/agency-ledger-summary');
export const createAgencyRequest = (data) => api.post('/distributor/agency-request', data);
export const getAgencyRequests = () => api.get('/distributor/agency-requests');
export const approveAgencyRequest = (id, data) => api.put(`/distributor/agency-requests/${id}/approve`, data);
export const rejectAgencyRequest = (id, data) => api.put(`/distributor/agency-requests/${id}/reject`, data);
export const createDistributorFundRequest = (data) => api.post('/distributor/fund-requests', data);
export const reviewDistributorFundRequest = (id, data) => api.put(`/distributor/fund-requests/${id}`, data);

// Distributor Files
export const uploadDistributorFile = (formData) => api.post('/distributor/files', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getDistributorFiles = () => api.get('/distributor/files');
export const deleteDistributorFile = (id) => api.delete(`/distributor/files/${id}`);

// Booking Files Workflow
export const uploadBookingFile = (formData) => api.post('/upload-booking-file', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const uploadBookingText = (data) => api.post('/submit-booking-text', data);
export const getBookingFiles = (paramsOrStatus, oilCompany) => {
  // Backwards compatible: accept either an object or positional (status, oilCompany)
  if (paramsOrStatus && typeof paramsOrStatus === 'object') {
    return api.get('/booking-files', { params: paramsOrStatus });
  }
  return api.get('/booking-files', { params: { status: paramsOrStatus, oil_company: oilCompany } });
};
export const getBookingFileDetail = (id) => api.get(`/booking-files/${id}`);
export const assignBookingFile = (id, data) => api.put(`/booking-files/${id}/assign`, data);
export const uploadProcessedFile = (id, formData) => api.put(`/booking-files/${id}/upload-processed`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const cancelBookingFileAssignment = (id, reason = '') => api.post(`/booking-files/${id}/cancel-assignment`, { reason });
export const parseProcessedFile = (id) => api.post(`/booking-files/${id}/parse-processed`);
export const completeBookingFile = (id, data) => api.post(`/booking-files/${id}/complete`, data);

// Booking History (1-Click Audit + Search Report)
export const searchBookingHistory = (params) => api.get('/booking-history/search', { params });
export const getBookingHistoryRow = (rowId) => api.get(`/booking-history/row/${rowId}`);

// Pending-report-upload notifier
export const runPendingReportNotifier = () => api.post('/pending-report-notifier/run');
export const getPendingReportStatus = () => api.get('/pending-report-notifier/status');
export const markAllNotificationsRead = () => api.put('/notifications/read-all');

// Consolidated Amazon/TataNeu account stock (admin-only)
export const getConsolidatedAccounts = (platform) => api.get('/platform-accounts/consolidated', { params: platform ? { platform } : {} });
export const updatePlatformAccount = (id, data) => api.patch(`/platform-accounts/${id}`, { data });

// Support Tickets
export const getSupportTickets = (params) => api.get('/support/tickets', { params });
export const getSupportTicketStats = () => api.get('/support/tickets/stats');
export const getSupportTicket = (id) => api.get(`/support/tickets/${id}`);
export const createSupportTicket = (data) => api.post('/support/tickets', data);
export const getMaxBookingQty = (agencyId, bookingDate, transactionId) => api.get('/support/max-booking-qty', { params: { agency_id: agencyId, booking_date: bookingDate, transaction_id: transactionId || '' } });
export const validateTxn = (transactionId, agencyId) => api.get('/support/validate-txn', { params: { transaction_id: transactionId, agency_id: agencyId } });
export const assignSupportTicket = (id, data) => api.put(`/support/tickets/${id}/assign`, data);
export const updateSupportTicketStatus = (id, data) => api.put(`/support/tickets/${id}/status`, data);

export default api;
export const resolveSupportTicket = (id, data) => api.post(`/support/tickets/${id}/resolve`, data);
export const addSupportTicketRemark = (id, remark) => api.post(`/support/tickets/${id}/remark`, { remark });
export const addLpgEntries = (id, entries) => api.post(`/support/tickets/${id}/lpg-entries`, { entries });
export const addLpgEntriesExcel = (id, formData) => api.post(`/support/tickets/${id}/lpg-entries/excel`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateLpgEntry = (id, lpgId, data) => api.put(`/support/tickets/${id}/lpg-entries/${encodeURIComponent(lpgId)}`, data);
export const escalateSupportTicket = (id) => api.put(`/support/tickets/${id}/escalate`);
export const getEscalationLevels = () => api.get('/support/escalation-levels');

// Card Statement Match (for Card Vendor complaints — bank PDF verification)
export const getCardStatementStatus = (ticketId) => api.get(`/card-statements/${ticketId}`);
export const parseCardComplaint = (ticketId) => api.post(`/card-statements/parse-complaint/${ticketId}`);
export const uploadCardStatement = (ticketId, formData) => api.post(`/card-statements/upload/${ticketId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const matchCardStatement = (ticketId) => api.post(`/card-statements/match/${ticketId}`);
export const resolveMatchedCardItems = (ticketId, data) => api.post(`/card-statements/${ticketId}/resolve-matched`, data || {});
export const sendCardItemsToBank = (ticketId, data) => api.post(`/card-statements/${ticketId}/send-to-bank`, data || {});
export const updateCardItemManual = (itemId, data) => api.patch(`/card-statements/item/${itemId}`, data);
export const updateCardItemBankResponse = (itemId, data) => api.patch(`/card-statements/item/${itemId}/bank-response`, data);
export const deleteCardStatement = (sid) => api.delete(`/card-statements/statement/${sid}`);

// BBPS — User endpoints
export const getBbpsCategories = () => api.get('/bbps/categories');
export const getBbpsBillers = (category) => api.get('/bbps/billers', { params: { category } });
export const fetchBbpsBill = (data) => api.post('/bbps/fetch-bill', data);
export const payBbpsBill = (data) => api.post('/bbps/pay', data);
export const getBbpsWallet = () => api.get('/bbps/wallet');
export const getBbpsWalletLedger = (limit = 50) => api.get('/bbps/wallet/ledger', { params: { limit } });
export const getBbpsTransactions = (params = {}) => api.get('/bbps/transactions', { params });

// BBPS — Admin endpoints
export const bbpsWalletTopup = (data) => api.post('/bbps-admin/wallet-topup', data);
export const bbpsApiTopup = (data) => api.post('/bbps-admin/api-balance/topup', data);
export const getBbpsApiBalance = () => api.get('/bbps-admin/api-balance');
export const getBbpsApiLedger = (limit = 100) => api.get('/bbps-admin/api-balance/ledger', { params: { limit } });
export const listBbpsWallets = (search) => api.get('/bbps-admin/wallets', { params: search ? { search } : {} });
export const searchBbpsUsers = (search) => api.get('/bbps-admin/users-searchable', { params: search ? { search } : {} });
export const getAllBbpsTransactions = (params = {}) => api.get('/bbps-admin/transactions', { params });
export const getBbpsStats = () => api.get('/bbps-admin/stats');
export const getBbpsReport = (kind, params = {}) => api.get(`/bbps-admin/report/${kind}`, { params });
export const downloadBbpsReportCsv = (kind, params = {}) => api.get(`/bbps-admin/report/${kind}/csv`, { params, responseType: 'blob' });
export const downloadBbpsReportPdf = (kind, params = {}) => api.get(`/bbps-admin/report/${kind}/pdf`, { params, responseType: 'blob' });
export const listBbpsAccess = () => api.get('/bbps-admin/access');
export const grantBbpsAccess = (data) => api.post('/bbps-admin/access/grant', data);
export const revokeBbpsAccess = (userId) => api.delete(`/bbps-admin/access/${userId}`);
export const bulkPayBbps = (data) => api.post('/bbps/bulk-pay', data);
export const getBbpsAccessStatus = () => api.get('/bbps/access-status');

// Payment gateway (Razorpay / Cashfree / PayU / Mock)
export const listPaymentGateways = () => api.get('/payments/gateways');
export const createPaymentOrder = (data) => api.post('/payments/create-order', data);
export const verifyPayment = (data) => api.post('/payments/verify', data);
export const getMyPaymentOrders = (limit = 50) => api.get('/payments/orders', { params: { limit } });

// Sales Persons
export const getSalesPersons = (params) => api.get('/sales-persons', { params });
export const createSalesPerson = (data) => api.post('/sales-persons', data);
export const updateSalesPerson = (id, data) => api.put(`/sales-persons/${id}`, data);
export const deleteSalesPerson = (id) => api.delete(`/sales-persons/${id}`);
export const getSalesPersonLedger = (params) => api.get('/sales-person-ledger', { params });
export const getSalesPersonLedgerStats = (params) => api.get('/sales-person-ledger/stats', { params });
export const recordSalesPersonPayment = (spId, data) => api.post(`/sales-person-ledger/payment?sp_id=${spId}`, data);
export const getSalesPersonDashboard = (params) => api.get('/sales-person/dashboard', { params });
export const assignSalesPersonToAgency = (agencyId, spId) => api.put(`/agencies/${agencyId}/assign-sales-person`, { sales_person_id: spId });

// KYC
export const getUserKYC = (userId) => api.get(`/users/${userId}/kyc`);
export const updateUserKYC = (userId, data) => api.put(`/users/${userId}/kyc`, data);
export const verifyUserKYC = (userId) => api.put(`/users/${userId}/kyc/verify`);

// Forgot Password
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (data) => api.post('/auth/reset-password', data);

export const createAccountant = (data) => api.post('/auth/create-accountant', data);
export const getPaymentRecovery = () => api.get('/payment-recovery');

// Platform Accounts
export const getPlatformAccounts = (params) => api.get('/platform-accounts', { params });
export const createPlatformAccount = (data) => api.post('/platform-accounts', data);
export const deletePlatformAccount = (id) => api.delete(`/platform-accounts/${id}`);
export const uploadPlatformAccounts = (platform, formData) => api.post(`/platform-accounts/upload?platform=${platform}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const downloadPlatformAccounts = (params) => api.get('/platform-accounts/download', { params, responseType: 'blob' });
export const getPlatformAccountLedger = (params) => api.get('/platform-account-ledger', { params });
export const createPlatformLedgerEntry = (data) => api.post('/platform-account-ledger', data);
export const getPlatformAccountStats = () => api.get('/platform-account-ledger/stats');

// Quick Booking (Semi-Automated)
export const getQuickBookingProviders = () => api.get('/quick-booking/providers');
export const startQuickBooking = (data) => api.post('/quick-booking/start', data);
export const markQuickBooking = (data) => api.post('/quick-booking/mark', data);
export const getBookingSessions = () => api.get('/quick-booking/sessions');

// Gift Cards
export const getGiftCards = (params) => api.get('/gift-cards', { params });
export const addGiftCards = (codes) => api.post('/gift-cards', { codes });
export const uploadGiftCards = (formData) => api.post('/gift-cards/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateGiftCard = (id, data) => api.put(`/gift-cards/${id}`, data);
export const deleteGiftCard = (id) => api.delete(`/gift-cards/${id}`);

// LPG IDs
export const getLpgIds = (params) => api.get('/lpg-ids', { params });
export const addLpgIds = (data) => api.post('/lpg-ids', data);
export const deleteLpgId = (id) => api.delete(`/lpg-ids/${id}`);

// Payment Cards
export const getPaymentCards = () => api.get('/payment-cards');
export const addPaymentCard = (data) => api.post('/payment-cards', data);
export const deletePaymentCard = (id) => api.delete(`/payment-cards/${id}`);
export const bulkAddCardsText = (data) => api.post('/payment-cards/bulk-text', data);
export const bulkAddCardsExcel = (fd) => api.post('/payment-cards/bulk-excel', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const downloadCardTemplate = () => api.get('/payment-cards/template', { responseType: 'blob' });
export const recordCardUsage = (data) => api.post('/payment-cards/use', data);

// Wizard Sessions
export const startWizard = (data) => api.post('/quick-booking/wizard/start', data);
export const updateWizardStep = (id, data) => api.put(`/quick-booking/wizard/${id}`, data);

// Automation
export const getAutomationConfig = () => api.get('/automation/config');
export const updateAutomationConfig = (data) => api.put('/automation/config', data);
export const createBatch = (data) => api.post('/automation/batch', data);
export const getBatches = () => api.get('/automation/batches');
export const getBatchDetail = (id) => api.get(`/automation/batches/${id}`);
export const controlBatch = (id, action) => api.put(`/automation/batches/${id}/control?action=${action}`);
export const deleteBatch = (id) => api.delete(`/automation/batches/${id}`);

export const getWizardSessions = () => api.get('/quick-booking/wizard/sessions');
export const getActiveWizard = () => api.get('/quick-booking/wizard/active');



// LPG Bill Checker
export const getLpgCheckerConfig = () => api.get('/lpg-checker/config');
export const updateLpgCheckerConfig = (data) => api.put('/lpg-checker/config', data);
export const fetchLpgBills = (formData) => api.post('/lpg-checker/fetch', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 });
export const fetchLpgBillsText = (data) => api.post('/lpg-checker/fetch-text', data);
export const getLpgCheckerSessions = () => api.get('/lpg-checker/sessions');
export const getLpgCheckerSession = (id) => api.get(`/lpg-checker/sessions/${id}`);
export const downloadLpgSession = (sessionId) => api.get(`/lpg-checker/sessions/${sessionId}/download`, { responseType: 'blob' });
export const downloadLpgProxy = () => api.get('/lpg-checker/download-proxy', { responseType: 'blob' });

// Agency Files (Admin manual upload on behalf of agencies)
export const submitAgencyFileText = (data) => api.post('/agency-files/submit-text', data);
export const uploadAgencyFile = (formData) => api.post('/agency-files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getAgencyFiles = (params) => api.get('/agency-files', { params });
export const getAgencyFileStats = () => api.get('/agency-files/stats');
export const assignAgencyFile = (id, data) => api.put(`/agency-files/${id}/assign`, data);
export const bulkAssignAgencyFiles = (data) => api.post('/agency-files/bulk-assign', data);
export const completeAgencyFile = (id, data) => api.put(`/agency-files/${id}/complete`, data);
export const deleteAgencyFile = (id) => api.delete(`/agency-files/${id}`);

// Invoices
export const getBookingInvoice = (bookingId) => api.get(`/invoices/booking/${bookingId}`, { responseType: 'blob' });
export const getDailyInvoice = (params) => api.get('/invoices/daily', { params, responseType: 'blob' });
export const getMonthlyInvoice = (params) => api.get('/invoices/monthly', { params, responseType: 'blob' });
export const getInvoiceDates = (params) => api.get('/invoices/list', { params });

// Settlements (Cash Settlement Tracking)
export const createSettlement = (data) => api.post('/settlements', data);
export const getSettlements = (params) => api.get('/settlements', { params });
export const getMonthlySettlementReport = (params) => api.get('/settlements/monthly-report', { params });
export const deleteSettlement = (id) => api.delete(`/settlements/${id}`);

// Payment Receipts (PayZapp style)
export const getPaymentReceipt = (bookingId) => api.get(`/receipts/booking/${bookingId}`, { responseType: 'blob' });

// Cashback / Ploughback Tracker
export const addCashbackEntry = (data) => api.post('/cashback/entry', data);
export const getCashbackEntries = () => api.get('/cashback');
export const deleteCashbackEntry = (id) => api.delete(`/cashback/${id}`);


// Vendor Recovery (deleted vendors)
export const listDeletedVendors = (days = 30) => api.get(`/vendors/deleted/recent?days=${days}`);
export const restoreVendor = (vendorId) => api.post(`/vendors/${vendorId}/restore`);
